import { Alert, Button, Checkbox, Chip, FormControl, FormControlLabel, FormGroup, IconButton, InputAdornment, InputLabel, Link, OutlinedInput, Paper, Slide, Switch, Typography } from "@mui/material";
import { ChipWrap } from "components/ChipWrap";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import axios from "axios";
import { useLogin } from "providers/Login";
import { useNotification } from "providers/Notification";
import DataTable, { GetSortString, SaveColumnsState, useTableColumnState } from "components/Table";
import StatusLabel from "components/StatusLabel";
import ConfirmDialog from "components/ConfirmDialog";
import ClearIcon from "@mui/icons-material/Clear";
import { useDebouncedValue } from "helpers/useDebouncedValue";
import { directoryDisplayName } from "routes/App/pages/Integrations/_components/Types";
import { groupKey } from "helpers/groupIdentity";

interface GroupsList {
    hasEnabledDirectory: boolean
    groups: Group[]
}

interface Group {
    id: string
    identifier: string
    provider: string
    directoryType: string
    directoryId: string
    directoryName: string
    createdAt: Date
    hidden: boolean
    usedIn?: string[]
}

type columnsType = "checkbox" | "identifier" | "directorytype" | "directoryname" | "usedby" | "createdat" | "hidden";

//Selection is either the rows the admin ticked or "every group matching the search".
//The second case deliberately ignores the "Show hidden groups" toggle: that toggle
//controls what the table displays, not what a bulk action covers, and an admin asking
//to select all groups means all of them. It cannot be an id list either - the customer
//with 80k groups would produce a request body past the server's 10MB limit - so it
//travels as the search itself and the server resolves it.
//SelectedRow carries the pair the API addresses a group by alongside its row id. The
//metadata travels with the selection because a row ticked on one page is gone from
//tableData once the admin pages away, and the bulk action still has to name it.
interface SelectedRow {
    id: string
    identifier: string
    provider: string
    directoryId: string
}

type Selection =
    | { mode: "rows", rows: SelectedRow[] }
    | { mode: "all", count: number, search: string };

const emptySelection: Selection = { mode: "rows", rows: [] };

function selectedRow(group: Group): SelectedRow {
    return { id: group.id, identifier: group.identifier, provider: group.provider, directoryId: group.directoryId };
}

function selectionCount(selection: Selection) {
    return selection.mode === "all" ? selection.count : selection.rows.length;
}

function isRowSelected(selection: Selection, id: string) {
    return selection.mode === "all" || selection.rows.some(x => x.id === id);
}

//pendingAction is the hide/show the confirm dialog is asking about. Only the "all"
//mode is confirmed - ticking rows by hand is already deliberate.
interface PendingAction {
    hidden: boolean
    selection: Extract<Selection, { mode: "all" }>
}

export default function Groups() {
    const notification = useNotification();
    const login = useLogin();
    const [search, setSearch] = useState("");
    const [finishedLoading, setFinishedLoading] = useState(false);
    const [showHidden, setShowHidden] = useState(false);
    const [selection, setSelection] = useState<Selection>(emptySelection);
    const [pendingAction, setPendingAction] = useState<PendingAction | undefined>();
    const [applying, setApplying] = useState(false);
    const [noAuthenticationSetup, setNoAuthenticationSetup] = useState(false);

    // pagination
    const [skipItems, setSkipItems] = useState(0);
    const [takeCount, setTakeCount] = useState(25);
    const [dataCount, setDataCount] = useState(0);
    //dataCount is what the table is showing; totalCount includes the hidden groups a
    //bulk action would also reach
    const [totalCount, setTotalCount] = useState(0);

    const [currentGroupList, setCurrentGroupListState] = useState<GroupsList | undefined>();

    const [tableData, setTableData] = useState<Group[]>();

    const req = login.GetAxios();

    const [columnsState, setColumnsState] = useTableColumnState<columnsType>([
        { columnId: "checkbox", width: "7%" },
        { columnId: "identifier", width: "18%" },
        { columnId: "directorytype", width: "13%" },
        { columnId: "directoryname", width: "15%" },
        { columnId: "usedby", width: "20%" },
        { columnId: "createdat", width: "15%", sortOrder: 1, direction: "desc" },
        { columnId: "hidden", width: "12%" },
    ]);

    const debouncedSearch = useDebouncedValue(search, 1000);

    useEffect(() => {
        //an "all matching" selection describes the filter it was made under, so it
        //stops being meaningful the moment that filter changes
        setSelection(emptySelection);
        loadData(columnsState, 0, takeCount, debouncedSearch, showHidden);
    }, [debouncedSearch, showHidden]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async (c: typeof columnsState, skip: number, take: number, search: string, showHidden: boolean) => {
        setFinishedLoading(false);
        if (!req) {
            return; //not logged in, nothing to do
        }
        try {
            const rules = await req.get("/api/rules");

            const rules_name_dict: { [k: string]: string } = {};

            for (let i of rules.data.items) {
                rules_name_dict[i.id] = i.name;
            }

            let sortBy = GetSortString(c);

            const resp = await req.get("/api/user/groups", {
                params: {
                    sortBy,
                    skip,
                    take,
                    search,
                    skiphidden: showHidden ? "" : "true",
                }
            });

            const data = {
                hasEnabledDirectory: resp.data.hasEnabledDirectory === true,
                groups: resp.data.groups.map((x: any) => {
                    return {
                        id: groupKey(x.directoryId, x.identifier),
                        identifier: x.identifier,
                        provider: x.provider,
                        directoryType: x.directoryType,
                        directoryId: x.directoryId,
                        directoryName: x.directoryName,
                        createdAt: new Date(x.createdAt),
                        hidden: x.hidden,
                        usedIn: x.usedIn?.map((x: string) => rules_name_dict[x] || "Unknown") || [],
                    };
                }),
            };

            setCurrentGroupListState(data);
            setTableData(data.groups);

            const cfg = await req.get("/api/config");
            setNoAuthenticationSetup(cfg.data?.authenticationMode === "none");

            setColumnsState(c);
            setSkipItems(skip);
            setTakeCount(take);
            setDataCount(resp.data.count || 0);
            setTotalCount(resp.data.total ?? resp.data.count ?? 0);
            setFinishedLoading(true);
        } catch (err) {
            console.error(err);
            notification.Display({
                title: "Error",
                message: "Could not fetch data",
                type: "error",
            });
        }
    };

    //applyHidden performs the bulk update. The "all" mode sends the filter rather than
    //the ids and can take a couple of seconds over a large directory, so the caller
    //blanks the table and holds the confirm dialog open until it resolves.
    const applyHidden = async (hide: boolean, target: Selection) => {
        if (!req) {
            return;
        }

        setApplying(true);
        setFinishedLoading(false);

        try {
            const body = target.mode === "all"
                ? { hidden: hide, all: { search: target.search, skipHidden: false } }
                : {
                    hidden: hide,
                    itens: target.rows.map(x => ({
                        identifier: x.identifier,
                        provider: x.provider,
                        directoryId: x.directoryId,
                    }))
                };

            const resp = await req.put("/api/user/groups/enabled", body);

            //the server reports what it actually changed, which is not the selection
            //size: groups already in the requested state are left alone
            const changed = resp.data?.changed ?? selectionCount(target);

            notification.Display({
                title: "Success",
                message: `${changed} ${changed === 1 ? "Group" : "Groups"} updated`,
                type: "success",
            });

            setSelection(emptySelection);
            setPendingAction(undefined);
            await loadData(columnsState, skipItems, takeCount, search, showHidden);
        } catch (err) {
            console.error(err);
            //a 409 means a directory sync landed mid-edit; the server's message tells
            //the admin to reload, which is more useful than a generic failure
            let message = "Could not update groups";
            if (axios.isAxiosError(err) && err.response && err.response.data?.message) {
                message = err.response.data.message;
            }
            notification.Display({
                title: "Error",
                message,
                type: "error",
            });
            setPendingAction(undefined);
            setFinishedLoading(true);
        } finally {
            setApplying(false);
        }
    };

    //requestHidden confirms first when the change would reach groups that are not on
    //screen, and applies straight away for a hand-picked set.
    const requestHidden = async (hide: boolean) => {
        if (selection.mode === "all") {
            setPendingAction({ hidden: hide, selection });
            return;
        }
        await applyHidden(hide, selection);
    };

    useEffect(() => {
        SaveColumnsState("groups", columnsState);
    }, [columnsState]);

    const pageRows = tableData || [];
    const selectedOnPage = pageRows.filter(x => isRowSelected(selection, x.id)).length;
    const selectedCount = selectionCount(selection);
    //offering "select all" only makes sense once the whole page is ticked and there is
    //more behind it than what is loaded - counting the hidden groups it would reach
    const canSelectAllMatching = selection.mode === "rows"
        && pageRows.length > 0
        && selectedOnPage === pageRows.length
        && totalCount > pageRows.length;
    //"matching this search" is only true when there is a search to match
    const allScopeLabel = debouncedSearch ? " matching this search" : "";

    return <>
        <Box display="flex">
            <Box>
                <Typography variant="h1">Groups</Typography>
            </Box>


            <FormControl variant="outlined" sx={{ marginLeft: "auto" }}>
                <InputLabel htmlFor="search-input">Search</InputLabel>
                <OutlinedInput id="search-input" label="Search"
                    value={search}
                    onChange={x => setSearch(x.target.value)}
                    disabled={applying || !currentGroupList?.hasEnabledDirectory}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton size="small" sx={{ marginLeft: "auto" }} onClick={() => {
                                setSearch("");
                            }}>
                                <ClearIcon />
                            </IconButton>
                        </InputAdornment>
                    }

                />
            </FormControl>

        </Box>
        <Box>
            <FormGroup>
                <FormControlLabel control={<Switch disabled={applying || !currentGroupList?.hasEnabledDirectory} checked={showHidden} onChange={() => { setShowHidden(!showHidden); }} />} label="Show hidden groups" />
            </FormGroup>
            {noAuthenticationSetup && <Alert severity="warning" sx={{ width: "100%", marginBottom: "10px", marginTop: "10px" }}><Typography>Group settings will have no effect without an authentication method enabled.</Typography></Alert>}
            {!currentGroupList?.hasEnabledDirectory && <Alert severity="error" sx={{ width: "100%", marginBottom: "10px", marginTop: "10px" }}><Typography>A directory integration must be configured and enabled before setting up groups.</Typography></Alert>}
        </Box>

        <DataTable
            data={tableData || []}
            finishedLoading={finishedLoading}
            columnsState={columnsState}
            onSortChanged={(x) => loadData(x, skipItems, takeCount, search, showHidden)}
            columnsDataId="datagrid_groupstable_columns"
            onPaginationStartItemChanged={(x) => {
                loadData(columnsState, x, takeCount, search, showHidden);
            }}
            onPaginationItemCountChanged={(x) => {
                loadData(columnsState, skipItems, x, search, showHidden);
            }}
            paginationStartItem={skipItems}
            paginationItemCount={takeCount}
            dataCount={dataCount}
            columns={{
                checkbox: {
                    displayName: "",
                    //the header cell only takes a string, so the select-all checkbox
                    //goes in the sub-header slot
                    renderSubHeader: () => {
                        return <Checkbox
                            id="select-all-checkbox"
                            disabled={applying || pageRows.length === 0}
                            checked={pageRows.length > 0 && selectedOnPage === pageRows.length}
                            indeterminate={selectedOnPage > 0 && selectedOnPage < pageRows.length}
                            onChange={() => {
                                if (selection.mode === "all") {
                                    setSelection(emptySelection);
                                    return;
                                }
                                const offPage = selection.rows.filter(x => !pageRows.some(y => y.id === x.id));
                                if (selectedOnPage === pageRows.length) {
                                    setSelection({ mode: "rows", rows: offPage });
                                    return;
                                }
                                setSelection({ mode: "rows", rows: offPage.concat(pageRows.map(selectedRow)) });
                            }}
                        />;
                    },
                    render: (x) => {
                        return <Checkbox
                            id={`group-checkbox-${x.id}`}
                            checked={isRowSelected(selection, x.id)}
                            disabled={applying}
                            onChange={() => {
                                //ticking a row off an "all matching" selection drops
                                //back to the rows on screen minus that one
                                if (selection.mode === "all") {
                                    setSelection({ mode: "rows", rows: pageRows.filter(y => y.id !== x.id).map(selectedRow) });
                                    return;
                                }
                                if (isRowSelected(selection, x.id)) {
                                    setSelection({ mode: "rows", rows: selection.rows.filter(y => y.id !== x.id) });
                                } else {
                                    setSelection({ mode: "rows", rows: selection.rows.concat(selectedRow(x)) });
                                }
                            }}
                        />;
                    }
                },
                identifier: {
                    displayName: "Name",
                    sortable: true,
                    render: (x) => {
                        return x.identifier;
                    },
                },
                directorytype: {
                    displayName: "Provider",
                    sortable: true,
                    render: (x) => {
                        return directoryDisplayName(x.directoryType || x.provider);
                    },
                },
                directoryname: {
                    displayName: "Directory",
                    sortable: true,
                    render: (x) => x.directoryName,
                },
                usedby: {
                    displayName: "Used By Rule(s)",
                    render: (x) => {
                        return <ChipWrap>
                            {(x.usedIn && x.usedIn.map(y => <Chip label={y.trim()} variant="outlined" size="small" color="primary" />))}
                        </ChipWrap>;
                    },
                },
                createdat: {
                    displayName: "Created At",
                    sortable: true,
                    render: (x) => {
                        return x.createdAt.toLocaleString();
                    },
                },
                hidden: {
                    displayName: "Show Group in Policy",
                    render: (x) => {
                        return (!x.hidden) ? <StatusLabel label="Yes" color="success" />
                            : <StatusLabel label="No" color="error" />;
                    },
                },
            }}
        />


        <Slide direction="up" in={selectedCount > 0} mountOnEnter unmountOnExit>
            <Box
                sx={{
                    position: "fixed",
                    minHeight: "60px",
                    width: "60vw",
                    left: "20vw",
                    bottom: "50px",
                }}

            >
                <Paper
                    elevation={3}
                    sx={{
                        display: "flex",
                        height: "100%",
                        padding: "10px",
                        alignItems: "center",
                    }}
                >
                    <Box>
                        <Typography>
                            {selection.mode === "all"
                                ? `All ${selectedCount.toLocaleString()} ${selectedCount === 1 ? "Group" : "Groups"}${allScopeLabel} selected`
                                : `${selectedCount} ${selectedCount === 1 ? "Group" : "Groups"} selected`}
                        </Typography>
                        {canSelectAllMatching && <Link
                            id="select-all-matching"
                            component="button"
                            variant="body2"
                            underline="always"
                            disabled={applying}
                            onClick={() => {
                                setSelection({
                                    mode: "all",
                                    count: totalCount,
                                    search: debouncedSearch,
                                });
                            }}
                        >
                            Select all {totalCount.toLocaleString()} groups{allScopeLabel}
                        </Link>}
                    </Box>
                    <Button
                        id="bulk-hide-button"
                        variant="outlined"
                        sx={{ marginLeft: "auto" }}
                        disabled={applying}
                        onClick={async () => {
                            await requestHidden(true);
                        }}
                    >
                        Hide Group in Policy
                    </Button>
                    <Button
                        id="bulk-show-button"
                        variant="outlined"
                        sx={{ marginLeft: "5px" }}
                        disabled={applying}
                        onClick={async () => {
                            await requestHidden(false);
                        }}
                    >
                        Show Group in Policy
                    </Button>
                    <Button
                        id="clear-selection-button"
                        variant="outlined"
                        sx={{ marginLeft: "5px" }}
                        disabled={applying}
                        onClick={() => {
                            setSelection(emptySelection);
                        }}
                    >
                        Clear selection
                    </Button>
                </Paper>
            </Box>
        </Slide>

        <ConfirmDialog
            open={pendingAction !== undefined}
            loading={applying}
            title={pendingAction?.hidden ? "Hide groups in policies?" : "Show groups in policies?"}
            confirmLabel={pendingAction?.hidden ? "Hide Them" : "Show Them"}
            confirmColor={pendingAction?.hidden ? "error" : "primary"}
            message={pendingAction && (pendingAction.hidden
                ? <>
                    This hides all {pendingAction.selection.count.toLocaleString()} groups
                    {pendingAction.selection.search ? " matching the current search" : ""} from policies,
                    including any not currently listed. Showing them again later un-hides every group,
                    including any you hid individually.
                </>
                : <>
                    This shows all {pendingAction.selection.count.toLocaleString()} groups
                    {pendingAction.selection.search ? " matching the current search" : ""} in policies,
                    including any that are currently hidden.
                </>)}
            onConfirm={async () => {
                if (!pendingAction) {
                    return;
                }
                await applyHidden(pendingAction.hidden, pendingAction.selection);
            }}
            onCancel={() => {
                setPendingAction(undefined);
            }}
        />

    </>;

}
